import { useCallback, useRef } from 'react';
import { useStytchB2BClient, useStytchMemberSession } from '@stytch/react/b2b';

/**
 * useSessionJwt — THE canonical way to consume auth in data-fetching code.
 *
 * The trap this exists to prevent: Stytch's heartbeat (~4 min) gives the
 * `session` object a new identity and rotates the JWT string. Any effect that
 * keys on the session object, a getTokens() memo, or the JWT value re-runs on
 * every heartbeat — silently refetching unchanged data app-wide.
 *
 * Contract:
 * - Key effects on `sessionId` — stable for the lifetime of a login.
 * - Read the token with `getJwt()` INSIDE the effect/handler at call time —
 *   always fresh, never a dependency.
 * - `isAuthenticated` includes Stytch's fromCache revalidation window
 *   (same semantics as ProtectedRoute) so UIs don't flap during refresh.
 *
 * Do NOT reintroduce: useMemo(() => stytch.session.getTokens(), [session]).
 */

/**
 * Pure latch rule (exported for testing) — the identity `useSessionJwt` returns.
 * Stytch's heartbeat revalidation transiently yields session=null with
 * fromCache=true; keying anything on the raw session id would flip X→null→X
 * every ~4 min and refetch. The latch holds identity across that window:
 * - a live member_session_id → adopt it (covers login and re-login)
 * - session null but fromCache → hold the previous id (revalidation window)
 * - session null and not fromCache → clear (a true logout)
 */
export function latchSessionId(prev, session, fromCache) {
  const liveId = session?.member_session_id ?? null;
  if (liveId) return liveId;
  if (!fromCache) return null;
  return prev;
}

export function useSessionJwt() {
  const stytch = useStytchB2BClient();
  const { session, fromCache } = useStytchMemberSession();

  // Latch the session id across the revalidation null window (see latchSessionId).
  const lastIdRef = useRef(null);
  lastIdRef.current = latchSessionId(lastIdRef.current, session, fromCache);
  const sessionId = lastIdRef.current;

  const getJwt = useCallback(
    () => stytch?.session.getTokens()?.session_jwt ?? null,
    [stytch]
  );

  // Force a server round-trip on the session. Returns true if the session is
  // alive (JWT refreshed as a side effect). On failure it clears the local
  // Stytch state so `session` goes null and ProtectedRoute shows the login
  // screen — the fix for "deep link to a logged-out window shows an empty
  // page shell". Callers: ProtectedRoute (once on mount) and data hooks on 401.
  const revalidateSession = useCallback(async () => {
    if (!stytch) return false;
    try {
      await stytch.session.authenticate();
      return true;
    } catch {
      try {
        await stytch.session.revoke({ forceClear: true });
      } catch {
        // Already dead server-side; forceClear still wipes local state.
      }
      return false;
    }
  }, [stytch]);

  return {
    sessionId,
    getJwt,
    isAuthenticated: !!session || fromCache,
    revalidateSession,
  };
}

export default useSessionJwt;
